import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { marketplaceApi, phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import MarketplaceHeader from "../components/MarketplaceHeader";
import ProductCard from "../components/ProductCard";
import FloatingTools from "../components/FloatingTools";
import Footer from "../components/Footer";
import { clearPurchaseDraft, loadPurchaseDraft, savePurchaseDraft } from "../utils/purchaseDrafts";

const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || "http://127.0.0.1:8000";

function normaliseImage(src) {
  if (!src) return "/images/product-electronics.svg";
  if (src.startsWith("http://") || src.startsWith("https://") || src.startsWith("/images/")) return src;
  if (src.startsWith("/media/")) return `${API_ORIGIN}${src}`;
  if (src.startsWith("media/")) return `${API_ORIGIN}/${src}`;
  return src;
}

function formatApiError(err) {
  const data = err?.response?.data;
  if (!data) return "Action failed. Please try again.";
  if (typeof data.detail === "string") return data.detail;
  return "Action failed. Please check the fields and try again.";
}

function PublicReviewCard({ review, isAuthenticated, onHelpful, onReport, onLogin }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("MISLEADING");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState("");

  async function helpful() {
    if (!isAuthenticated) return onLogin();
    setBusy("helpful");
    try { await onHelpful(review); } finally { setBusy(""); }
  }

  async function report(event) {
    event.preventDefault();
    if (!isAuthenticated) return onLogin();
    setBusy("report");
    try {
      await onReport(review, { reason, details });
      setReportOpen(false);
      setDetails("");
    } finally {
      setBusy("");
    }
  }

  return (
    <article className="public-review-card">
      <header><div><strong>{review.reviewer_name}</strong><span>{review.verified_purchase ? "Verified purchase" : "Customer review"}</span></div><div className="public-review-stars">{"★".repeat(Number(review.product_rating || 0))}{"☆".repeat(5 - Number(review.product_rating || 0))}</div></header>
      <h3>{review.title}</h3>
      <p>{review.comment}</p>
      <div className="public-review-meta"><span>Product {review.product_rating}/5</span><span>Seller {review.seller_rating}/5</span>{review.delivery_rating ? <span>Delivery {review.delivery_rating}/5</span> : null}</div>
      {review.merchant_response ? <div className="public-review-response"><strong>Seller response</strong><p>{review.merchant_response}</p></div> : null}
      {review.transporter_response ? <div className="public-review-response"><strong>Transporter response</strong><p>{review.transporter_response}</p></div> : null}
      <footer><button type="button" className={review.is_helpful ? "active" : ""} disabled={busy === "helpful"} onClick={helpful}>Helpful ({review.helpful_count || 0})</button><button type="button" onClick={() => isAuthenticated ? setReportOpen((value) => !value) : onLogin()}>Report</button></footer>
      {reportOpen ? <form className="public-review-report" onSubmit={report}><select value={reason} onChange={(event) => setReason(event.target.value)}><option value="SPAM">Spam</option><option value="OFFENSIVE">Offensive content</option><option value="MISLEADING">Misleading</option><option value="PERSONAL_INFORMATION">Personal information</option><option value="OTHER">Other</option></select><input value={details} onChange={(event) => setDetails(event.target.value)} placeholder="Optional details" /><button type="submit" disabled={busy === "report"}>{busy === "report" ? "Sending..." : "Send report"}</button></form> : null}
    </article>
  );
}

function InlineReviewStars({ label, value, onChange }) {
  return (
    <div className="review-star-field">
      <label>{label}</label>
      <div className="review-stars" aria-label={`${label}: ${value || 0} out of 5`}>
        {[1, 2, 3, 4, 5].map((star) => <button key={star} type="button" className={star <= Number(value || 0) ? "active" : ""} onClick={() => onChange(star)} aria-label={`${star} star${star === 1 ? "" : "s"}`}>★</button>)}
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [payload, setPayload] = useState({ product: null, related_products: [] });
  const [loading, setLoading] = useState(true);
  const [activeFlow, setActiveFlow] = useState("ORDER");
  const [form, setForm] = useState({ quantity: 1, delivery_area: "", message: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [reviewEligibility, setReviewEligibility] = useState([]);
  const [reviewEligibilityLoaded, setReviewEligibilityLoaded] = useState(false);
  const [reviewModal, setReviewModal] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewNotice, setReviewNotice] = useState("");
  const [reviewForm, setReviewForm] = useState({ product_rating: 0, seller_rating: 0, delivery_rating: 0, title: "", comment: "" });
  const draftKey = `product:${slug}`;

  useEffect(() => {
    const draft = loadPurchaseDraft(draftKey);
    setForm(draft?.form ? { quantity: 1, delivery_area: "", message: "", ...draft.form } : { quantity: 1, delivery_area: "", message: "" });
    setActiveFlow(["ORDER", "ENQUIRY"].includes(draft?.activeFlow) ? draft.activeFlow : "ORDER");
  }, [draftKey]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    marketplaceApi.productDetail(slug)
      .then((res) => {
        if (mounted) {
          setPayload(res.data || {});
          const src = res.data?.product?.image_src || res.data?.product?.image_url;
          setSelectedImage(normaliseImage(src));
        }
      })
      .catch(() => { if (mounted) setError("Product could not be loaded from the marketplace."); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  useEffect(() => {
    if (!isAuthenticated || user?.account_type !== "BUYER" || !payload.product?.id) {
      setReviewEligibility([]);
      setReviewEligibilityLoaded(Boolean(isAuthenticated));
      return;
    }
    let mounted = true;
    setReviewEligibilityLoaded(false);
    phase1Api.reviewEligibility()
      .then((res) => { if (mounted) setReviewEligibility(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (mounted) setReviewEligibility([]); })
      .finally(() => { if (mounted) setReviewEligibilityLoaded(true); });
    return () => { mounted = false; };
  }, [isAuthenticated, user?.account_type, payload.product?.id]);

  const product = payload.product;
  const productReviewEligibility = useMemo(() => reviewEligibility.find((item) => Number(item.product) === Number(product?.id)), [reviewEligibility, product?.id]);
  const image = selectedImage || normaliseImage(product?.image_src || product?.image_url);
  const gallery = useMemo(() => {
    const main = normaliseImage(product?.image_src || product?.image_url);
    const images = product?.images?.map((img) => normaliseImage(img.image_url || img.image)) || [];
    return [main, ...images.filter((item) => item && item !== main)].slice(0, 5);
  }, [product]);

  useEffect(() => {
    if (!reviewEligibilityLoaded || !isAuthenticated || user?.account_type !== "BUYER" || !product?.id) return;
    const requestedReview = new URLSearchParams(window.location.search).get("review") === "1";
    if (!requestedReview) return;
    if (productReviewEligibility && !productReviewEligibility.already_reviewed) {
      setReviewModal(true);
      setReviewNotice("");
    } else if (productReviewEligibility?.already_reviewed) {
      setReviewNotice("You have already reviewed this completed purchase.");
    } else {
      setReviewNotice("You can review this product after an order for it has been completed.");
    }
    navigate(`/products/${slug}#reviews`, { replace: true });
  }, [reviewEligibilityLoaded, isAuthenticated, user?.account_type, product?.id, productReviewEligibility, navigate, slug]);

  function update(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function requireBuyer() {
    if (!isAuthenticated) {
      savePurchaseDraft(draftKey, { form, activeFlow });
      const next = `${window.location.pathname}${window.location.search || ""}`;
      navigate(`/login?next=${encodeURIComponent(next)}`);
      return false;
    }
    if (["SELLER", "BUSINESS", "COURIER"].includes(user?.account_type)) {
      setError("Please use a buyer account to place a marketplace order or purchase enquiry.");
      return false;
    }
    return true;
  }

  async function sendEnquiry(event) {
    event.preventDefault();
    if (!product || !requireBuyer()) return;
    setBusy(true);
    setMessage("");
    setError("");
    try {
      await marketplaceApi.createProductEnquiry({
        product: product.id,
        quantity: Number(form.quantity) || 1,
        preferred_delivery_area: form.delivery_area,
        message: form.message || "Buyer needs seller confirmation before ordering.",
      });
      clearPurchaseDraft(draftKey);
      setMessage("Your enquiry has been sent. Open Dashboard → Enquiries to follow the seller response.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function placeDirectOrder(event) {
    event.preventDefault();
    if (!product || !requireBuyer()) return;
    setBusy(true);
    setMessage("");
    setError("");
    try {
      await marketplaceApi.createDirectOrder(product.slug, {
        quantity: Number(form.quantity) || 1,
        delivery_area: form.delivery_area,
        notes: form.message || "Buyer placed a direct marketplace order.",
      });
      clearPurchaseDraft(draftKey);
      setMessage("Order created. Opening Orders so you can choose a payment option and track progress.");
      setTimeout(() => navigate("/portal/orders"), 700);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  function loginForReviewAction() {
    const next = `/products/${slug}?review=1#reviews`;
    navigate(`/login?next=${encodeURIComponent(next)}`);
  }

  function loginBackToProduct() {
    const next = `/products/${slug}#reviews`;
    navigate(`/login?next=${encodeURIComponent(next)}`);
  }

  function openProductReview() {
    if (!isAuthenticated) return loginForReviewAction();
    if (user?.account_type !== "BUYER") {
      setReviewNotice("Product reviews can only be submitted from a buyer account.");
      return;
    }
    if (!productReviewEligibility) {
      setReviewNotice("You can review this product after an order for it has been completed.");
      return;
    }
    if (productReviewEligibility.already_reviewed) {
      setReviewNotice("You have already reviewed this completed purchase.");
      return;
    }
    setReviewForm({ product_rating: 0, seller_rating: 0, delivery_rating: 0, title: "", comment: "" });
    setReviewNotice("");
    setReviewModal(true);
  }

  async function submitProductReview(event) {
    event.preventDefault();
    if (!productReviewEligibility) return;
    setReviewBusy(true);
    setReviewNotice("");
    try {
      await phase1Api.createReview({
        order: productReviewEligibility.order,
        product: productReviewEligibility.product,
        product_rating: reviewForm.product_rating,
        seller_rating: reviewForm.seller_rating,
        delivery_rating: productReviewEligibility.courier_name && reviewForm.delivery_rating ? reviewForm.delivery_rating : null,
        title: reviewForm.title,
        comment: reviewForm.comment,
      });
      const refreshed = await marketplaceApi.productDetail(slug);
      setPayload(refreshed.data || {});
      setReviewEligibility((items) => items.map((item) => Number(item.product) === Number(product.id) ? { ...item, already_reviewed: true } : item));
      setReviewModal(false);
      setReviewNotice("Thank you. Your verified purchase review is now visible on this product.");
      setTimeout(() => document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (err) {
      const data = err?.response?.data;
      setReviewNotice(data?.detail || data?.title || data?.comment || data?.product_rating || "The review could not be submitted.");
    } finally {
      setReviewBusy(false);
    }
  }

  async function markHelpful(review) {
    try {
      const res = await marketplaceApi.markReviewHelpful(review.id);
      setPayload((prev) => ({ ...prev, reviews: (prev.reviews || []).map((item) => item.id === review.id ? { ...item, helpful_count: res.data.helpful_count, is_helpful: res.data.selected } : item) }));
    } catch (err) {
      setError(formatApiError(err));
    }
  }

  async function reportReview(review, report) {
    try {
      await marketplaceApi.reportReview(review.id, report);
      setMessage("Thank you. The review has been sent to TAZY CART for moderation.");
    } catch (err) {
      setError(formatApiError(err));
    }
  }

  if (loading) return <div className="page-shell"><MarketplaceHeader /><main className="container detail-loading">Loading product...</main></div>;
  if (!product) return <div className="page-shell"><MarketplaceHeader /><main className="container detail-loading">{error || "Product not found."}</main></div>;

  return (
    <div className="page-shell product-detail-page" id="top">
      <MarketplaceHeader apiOnline />
      <main className="container product-detail-pro-layout">
        <section className="product-gallery-pro-card">
          <div className="product-gallery-main-pro"><img src={image} alt={product.title} /></div>
          <div className="product-gallery-thumbs-pro">
            {gallery.map((item, index) => (
              <button key={`${item}-${index}`} type="button" className={item === image ? "active" : ""} onClick={() => setSelectedImage(item)}>
                <img src={item} alt="Product thumbnail" />
              </button>
            ))}
          </div>
        </section>

        <section className="product-info-pro-card">
          <Link className="back-link" to="/">← Back to marketplace</Link>
          <div className="detail-badges"><span>{product.category_name || "Marketplace"}</span><span>{product.availability_label || "Available"}</span><span>{product.verified_badge || "Verified seller"}</span></div>
          <h1>{product.title}</h1>
          <p className="product-detail-description-pro">{product.short_description || "Marketplace listing from a verified supplier."}</p>
          <div className="detail-price">{product.price_range}</div>
          <div className="product-detail-rating"><span>★</span><strong>{Number(payload.review_summary?.average || product.rating || 0).toFixed(1)}</strong><em>{payload.review_summary?.count || product.review_count || 0} verified review(s)</em></div>
          <div className="detail-spec-grid detail-spec-grid-pro">
            <div><span>MOQ</span><strong>{product.moq_text || "Flexible"}</strong></div>
            <div><span>Brand</span><strong>{product.brand || "—"}</strong></div>
            <div><span>Availability</span><strong>{product.availability_label || "Available"}</strong></div>
            <div><span>Location</span><strong>{product.supplier_location || product.country || "ZW"}</strong></div>
          </div>
          <div className="seller-contact-card seller-contact-card-pro product-linked-seller-card">
            <div className="seller-mini-avatar">{product.seller_logo_src ? <img src={normaliseImage(product.seller_logo_src)} alt="Seller logo" /> : (product.seller_display || "S")[0]}</div>
            <div><span>Seller</span><strong>{product.seller_display || product.seller_name || "Marketplace seller"}</strong><p>This purchase request will be linked to seller and product code {product.code}.</p></div>
          </div>

          <div className="checkout-panel-v2 checkout-panel-pro">
            <div className="checkout-tabs-v2">
              <button type="button" className={activeFlow === "ORDER" ? "active" : ""} onClick={() => setActiveFlow("ORDER")}>Place order</button>
              <button type="button" className={activeFlow === "ENQUIRY" ? "active" : ""} onClick={() => setActiveFlow("ENQUIRY")}>Ask seller first</button>
            </div>
            <p className="checkout-helper-line">{activeFlow === "ORDER" ? "After placing the order, you will choose Pay on delivery, EcoCash, OneMoney, bank transfer or another enabled method from your Orders workspace." : "Ask the seller first when you need confirmation before ordering."}</p>
            <form className="buying-enquiry-grid checkout-form-v2 checkout-form-pro" onSubmit={activeFlow === "ORDER" ? placeDirectOrder : sendEnquiry}>
              <div><label>Quantity needed</label><input type="number" min="1" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} /></div>
              <div><label>Delivery area</label><input value={form.delivery_area} onChange={(e) => update("delivery_area", e.target.value)} placeholder="City / suburb / pickup point" /></div>
              <div className="full"><label>{activeFlow === "ORDER" ? "Order note" : "Message to seller"}</label><textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder={activeFlow === "ORDER" ? "Optional note for seller before confirmation." : "Ask about availability, final price, delivery or payment terms."} /></div>
              {message && <div className="success-box product-action-message full">{message}</div>}
              {error && <div className="error-box product-action-message full">{error}</div>}
              <div className="detail-actions detail-actions-v2 full"><button type="submit" disabled={busy} className="secondary-action">{activeFlow === "ORDER" ? "Place order through TAZY CART" : "Send enquiry through TAZY CART"}</button></div>
            </form>
          </div>
        </section>
      </main>

      <section className="container public-product-reviews-section" id="reviews">
        <div className="section-heading"><div><h2>Verified customer reviews</h2><p>Feedback from buyers with completed TAZY CART orders.</p></div><div className="product-review-heading-actions">{!isAuthenticated || user?.account_type === "BUYER" ? <button type="button" onClick={openProductReview}>{isAuthenticated ? productReviewEligibility?.already_reviewed ? "Review submitted" : "Write a review" : "Sign in to review"}</button> : null}{isAuthenticated ? <Link to="/portal/reviews">{user?.account_type === "BUYER" ? "My reviews" : "View reviews"}</Link> : null}</div></div>
        {reviewNotice ? <div className="product-review-notice">{reviewNotice}</div> : null}
        <div className="review-summary-panel">
          <div className="review-summary-score"><strong>{Number(payload.review_summary?.average || 0).toFixed(1)}</strong><span>★★★★★</span><p>{payload.review_summary?.count || 0} verified review(s)</p></div>
          <div className="review-distribution">{[5, 4, 3, 2, 1].map((star) => { const count = Number(payload.review_summary?.distribution?.[String(star)] || 0); const total = Number(payload.review_summary?.count || 0); return <div key={star}><span>{star} star</span><b><i style={{ width: `${total ? (count / total) * 100 : 0}%` }} /></b><em>{count}</em></div>; })}</div>
        </div>
        <div className="public-review-grid">{payload.reviews?.length ? payload.reviews.map((review) => <PublicReviewCard key={review.id} review={review} isAuthenticated={isAuthenticated} onHelpful={markHelpful} onReport={reportReview} onLogin={loginBackToProduct} />) : <div className="marketplace-empty-feed">No reviews yet. The first verified buyer review will appear here.</div>}</div>
      </section>

      <section className="container products-section products-section--compact related-detail-listings">
        <div className="section-heading"><div><h2>Related marketplace listings</h2><p>Products from the same category or seller network.</p></div></div>
        <div className="product-grid product-grid--compact product-grid-v2">{payload.related_products?.length ? payload.related_products.map((item) => <ProductCard key={item.id} item={item} />) : <div className="marketplace-empty-feed">No related listings yet.</div>}</div>
      </section>
      <Footer />
      <FloatingTools />

      {reviewModal && productReviewEligibility ? (
        <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
          <section className="tazy-modal-card review-compose-modal product-review-compose-modal">
            <header className="tazy-modal-header"><div><span>VERIFIED PURCHASE</span><h3>Review {product.title}</h3></div><button type="button" onClick={() => setReviewModal(false)}>×</button></header>
            <form onSubmit={submitProductReview}>
              <div className="review-compose-summary"><strong>{productReviewEligibility.order_number}</strong><span>{productReviewEligibility.seller_name}</span>{productReviewEligibility.courier_name ? <span>Delivered by {productReviewEligibility.courier_name}</span> : null}</div>
              <InlineReviewStars label="Product quality" value={reviewForm.product_rating} onChange={(value) => setReviewForm((prev) => ({ ...prev, product_rating: value }))} />
              <InlineReviewStars label="Seller service" value={reviewForm.seller_rating} onChange={(value) => setReviewForm((prev) => ({ ...prev, seller_rating: value }))} />
              {productReviewEligibility.courier_name ? <InlineReviewStars label="Delivery service" value={reviewForm.delivery_rating} onChange={(value) => setReviewForm((prev) => ({ ...prev, delivery_rating: value }))} /> : null}
              <div><label>Review title</label><input maxLength="140" value={reviewForm.title} onChange={(event) => setReviewForm((prev) => ({ ...prev, title: event.target.value }))} placeholder="Summarise your experience" /></div>
              <div><label>Your review</label><textarea value={reviewForm.comment} onChange={(event) => setReviewForm((prev) => ({ ...prev, comment: event.target.value }))} placeholder="What went well? What should other buyers know?" /></div>
              <p className="review-guidance">Keep feedback honest and based on this purchase. Do not include phone numbers or private information.</p>
              {reviewNotice ? <div className="error-box">{reviewNotice}</div> : null}
              <div className="review-compose-actions"><button type="button" onClick={() => setReviewModal(false)}>Cancel</button><button type="submit" disabled={reviewBusy || !reviewForm.product_rating || !reviewForm.seller_rating || reviewForm.title.trim().length < 3 || reviewForm.comment.trim().length < 10}>{reviewBusy ? "Publishing..." : "Publish review"}</button></div>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}
